import { callLambdaFunction } from '../aiClient';

/**
 * Lambda endpoints configuration
 */
const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;
const ENV_API_BASE_URL = String(import.meta.env?.VITE_API_BASE_URL || '').trim();
const CURRENT_HOSTNAME = typeof window !== 'undefined'
  ? String(window.location.hostname || '').toLowerCase()
  : '';
const IS_LOCAL_DEV = Boolean(
  import.meta.env?.DEV
  && typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(CURRENT_HOSTNAME)
);
const DEFAULT_PHP_CHAT_ENDPOINT = '/assistant_chat_completion.php';
const CHAT_PROXY_ENDPOINT = IS_LOCAL_DEV
  ? DEFAULT_PHP_CHAT_ENDPOINT
  : (ENV_API_BASE_URL
    ? `${ENV_API_BASE_URL.replace(/\/+$/, '')}/assistant_chat_completion.php`
    : DEFAULT_PHP_CHAT_ENDPOINT);
const CHAT_LOCAL_FALLBACK_ENABLED = String(import.meta.env?.VITE_CHAT_LOCAL_FALLBACK ?? 'true') !== 'false';

function isPhpChatProxyEndpoint(endpoint) {
  return String(endpoint || '').toLowerCase().includes('assistant_chat_completion.php');
}

function getLastUserMessage(messages = []) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role === 'user' && typeof msg?.content === 'string' && msg.content.trim()) {
      return msg.content.trim();
    }
  }

  return '';
}

function normalizeText(value = '') {
  return String(value || '').trim().toLowerCase();
}

function isGreeting(text = '') {
  const normalized = normalizeText(text);
  if (!normalized) return false;

  return /^(oi|ol[áa]|ola|bom\s+dia|boa\s+tarde|boa\s+noite|hello|hi|hey)\b/.test(normalized);
}

function containsSecondCopyIntent(text = '') {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  return /segunda\s*via|2\s*via|reimpress|reemitir|emitir\s+novamente/.test(normalized);
}

function isPayrollQuery(text = '') {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  return /folha\s+salari[áa]l|folha\s+de\s+pagamento|sal[aá]rio|recibo\s+salari(al)?|pagamento(s)?|feria(s)?|f[ée]rias|rh\b|recursos\s+humanos/.test(normalized);
}

function detectDocumentTypeHint(text = '') {
  const normalized = normalizeText(text);
  if (!normalized) return '';

  if (/\bft\b|fatura|factura/.test(normalized)) return 'FT';
  if (/\bfr\b/.test(normalized)) return 'FR';
  if (/\bpp\b|proforma|pro\s*forma/.test(normalized)) return 'PP';
  if (/\bor\b|orcamento|orçamento/.test(normalized)) return 'OR';
  if (/retific/.test(normalized)) return 'RETIFICACAO';
  if (/cancel/.test(normalized)) return 'CANCELAMENTO';

  return '';
}

function buildLocalFallbackContent(messages = []) {
  const userText = getLastUserMessage(messages);
  const documentTypeHint = detectDocumentTypeHint(userText);

  if (containsSecondCopyIntent(userText)) {
    return [
      'Posso orientar no fluxo exato para FT, FR, PP, OR, retificacao ou cancelamento.',
      'Antes de finalizar, confirme estes pontos rapidos:',
      '- Caixa aberta no POS e sessao valida (empresa/loja/utilizador).',
      '- Serie ativa para o documento selecionado.',
      '- Itens validos no carrinho e regras fiscais respeitadas.',
      '',
      'Apos a confirmacao, indique o tipo de documento para eu fornecer o procedimento exato de segunda via.',
    ].join('\n');
  }

  if (!userText) {
    return [
      'Objetivo: orientar no procedimento dentro do Dissa Ax.',
      'Pre-requisitos:',
      '- Sessao ativa no modulo correto.',
      '- Permissao para a operacao.',
      '',
      'Proxima acao: descreva a operacao em uma frase (ex.: emitir segunda via FT).',
    ].join('\n');
  }

  if (!documentTypeHint && isGreeting(userText)) {
    return [
      'Olá! Sou assistente do Dissa Ax.',
      'Posso ajudar com faturação, vendas, compliance, folha salarial ou outros procedimentos do sistema.',
      'Diga em uma frase o que deseja fazer ou qual modulo deseja usar.',
    ].join('\n');
  }

  if (!documentTypeHint && isPayrollQuery(userText)) {
    return [
      'Posso ajudar com procedimentos de folha salarial e RH no Dissa Ax, como processar pagamento, emitir recibo salarial ou consultar historico.',
      'Descreva em uma frase o que pretende fazer no modulo de folha salarial.',
    ].join('\n');
  }

  const nextQuestion = documentTypeHint
    ? `Confirme o modulo/tela onde esta (${documentTypeHint}) para eu enviar os passos exatos.`
    : 'Descreva o processo que deseja realizar no Dissa Ax para que eu possa orientar o procedimento correto.';

  return [
    'Objetivo: orientar o procedimento solicitado no Dissa Ax.',
    'Pre-requisitos:',
    '- Sessao valida no escopo correto (empresa/loja/utilizador).',
    '- Serie ativa e permissao para a operacao.',
    '',
    `Proxima acao: ${nextQuestion}`,
  ].join('\n');
}

function buildLocalFallbackResponse(messages = []) {
  return {
    provider: 'LOCAL_FALLBACK',
    model: 'local-template',
    choices: [
      {
        message: {
          role: 'assistant',
          content: buildLocalFallbackContent(messages),
        },
      },
    ],
  };
}

function resolveChatCompletionEndpoint() {
  const endpoint = String(CHAT_COMPLETION_ENDPOINT || '').trim();
  if (!endpoint) {
    const proxyEndpoint = String(CHAT_PROXY_ENDPOINT || '').trim();
    if (proxyEndpoint) {
      return proxyEndpoint;
    }

    if (CHAT_LOCAL_FALLBACK_ENABLED) {
      return null;
    }

    throw new Error('CONFIG_MISSING: VITE_AWS_LAMBDA_CHAT_COMPLETION_URL nao esta configurada no frontend.');
  }

  return endpoint;
}

/**
 * Get chat completion from any AI provider
 * 
 * @param {string} provider - Provider identifier (e.g., 'ANTHROPIC', 'OPENAI')
 * @param {string} model - Model name
 * @param {array} messages - Messages array
 * @param {object} options - Additional parameters
 */
export async function getChatCompletion(provider, model, messages, options = {}) {
  const endpoint = resolveChatCompletionEndpoint();
  if (!endpoint) {
    return buildLocalFallbackResponse(messages);
  }

  const payload = {
    provider,
    model,
    messages,
    stream: false,
    parameters: options,
  };

  try {
    const response = await callLambdaFunction(
      endpoint,
      payload
    );

    // Return full SDK response for maximum flexibility
    return response;
  } catch (error) {
    if (CHAT_LOCAL_FALLBACK_ENABLED) {
      return buildLocalFallbackResponse(messages);
    }

    throw error;
  }
}

/**
 * Stream chat completion from any AI provider
 */
export async function getStreamingChatCompletion(
  provider,
  model,
  messages,
  onChunk,
  onComplete,
  onError,
  options = {}
) {
  const endpoint = resolveChatCompletionEndpoint();
  if (!endpoint) {
    const fallback = buildLocalFallbackResponse(messages);
    const content = fallback?.choices?.[0]?.message?.content || '';
    if (content) {
      onChunk({ choices: [{ delta: { content } }] });
    }
    onComplete();
    return;
  }

  if (isPhpChatProxyEndpoint(endpoint)) {
    try {
      const response = await callLambdaFunction(endpoint, {
        provider,
        model,
        messages,
        stream: false,
        parameters: options,
      });

      const content = response?.choices?.[0]?.message?.content || response?.message?.content || '';
      if (content) {
        onChunk({ choices: [{ delta: { content } }] });
      }
      onComplete();
      return;
    } catch (error) {
      console.error('Streaming fallback error:', error);
      if (CHAT_LOCAL_FALLBACK_ENABLED) {
        const fallback = buildLocalFallbackResponse(messages);
        const content = fallback?.choices?.[0]?.message?.content || '';
        if (content) {
          onChunk({ choices: [{ delta: { content } }] });
        }
        onComplete();
        return;
      }

      onError(error);
      return;
    }
  }

  const payload = {
    provider,
    model,
    messages,
    stream: true,
    parameters: options,
  };

  try {
    // Attempt to include session cookie and Authorization token (if stored in localStorage)
    let authToken = null;
    try {
      if (typeof window !== 'undefined') {
        authToken = localStorage.getItem('dissaax_session_token') || localStorage.getItem('authToken') || localStorage.getItem('token');
      }
    } catch (e) {
      authToken = null;
    }

    const headers = { 'Content-Type': 'application/json' };
    if (authToken) headers.Authorization = `Bearer ${authToken}`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!response?.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader?.read();
      if (done) break;

      buffer += decoder?.decode(value, { stream: true });
      const lines = buffer?.split('\n');
      buffer = lines?.pop() || '';

      for (const line of lines) {
        if (line?.startsWith('data: ')) {
          try {
            const data = JSON.parse(line?.slice(6));
            
            if (data?.type === 'chunk' && data?.chunk) {
              onChunk(data?.chunk);
            } else if (data?.type === 'done') {
              onComplete();
            } else if (data?.type === 'error') {
              console.error('Lambda Function Error:', {
                error: data?.error,
                details: data?.details,
              });
              onError(new Error(data.error));
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    onError(error);
  }
}